import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { openWhatsApp } from '../lib/whatsapp';
import { useTenantBranding } from '../contexts/TenantBrandingContext';
import InstallAppCard from '../components/InstallAppCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

type ResetStep =
  | 'none'
  | 'enter-phone'         // member enters phone for forgot-password
  | 'email-sent'          // success: reset email dispatched
  | 'no-email';           // member has no email → show studio contact CTA

const MemberLogin = () => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const branding = useTenantBranding();

  const [resetStep, setResetStep] = useState<ResetStep>('none');
  const [resetPhone, setResetPhone] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [support, setSupport] = useState<{ studio_name: string; phone?: string; whatsapp?: string }>({ studio_name: '' });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/member-auth/login`, { phone, password });
      const { access_token, member } = response.data;
      localStorage.setItem('member_token', access_token);
      localStorage.setItem('member', JSON.stringify(member));
      if (member.must_change_password) {
        navigate('/member-force-change-password', { replace: true });
      } else {
        toast.success(`Hoş geldiniz, ${member.name}!`);
        navigate('/member-portal', { replace: true });
      }
    } catch (error: any) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('Üye bulunamadı') || detail.includes('not found')) {
        toast.error('Bu telefon numarası ile kayıtlı üye bulunamadı.');
      } else {
        toast.error(detail || 'Giriş başarısız');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetPhone.trim()) {
      toast.error('Telefon numaranızı girin');
      return;
    }
    setResetLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/member-auth/forgot-password`, { phone: resetPhone.trim() });
      const { has_email, masked_email, support: supportInfo } = res.data;
      if (has_email) {
        setMaskedEmail(masked_email || '');
        setResetStep('email-sent');
      } else {
        setSupport(supportInfo || { studio_name: 'Stüdyo Yönetimi' });
        setResetStep('no-email');
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        toast.error('Çok fazla istek. Birkaç dakika sonra tekrar deneyin.');
      } else {
        toast.error(err.response?.data?.detail || 'İşlem başarısız');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleContactStudio = () => {
    const contactNumber = support.whatsapp || support.phone || '';
    if (!contactNumber) {
      toast.error('Stüdyo iletişim numarası bulunamadı. Lütfen stüdyonuza bizzat başvurun.');
      return;
    }
    const message = `Merhaba ${support.studio_name || ''},\n\nÜye portalı şifremi sıfırlamak istiyorum ancak hesabıma kayıtlı e-posta adresim yok.\n\nTelefon numaram: ${resetPhone}\n\nE-posta adresimi sisteme eklemeniz için sizinle iletişime geçiyorum. Teşekkürler.`;
    openWhatsApp(contactNumber, message);
  };

  const resetForm = () => {
    setResetStep('none');
    setResetPhone('');
    setMaskedEmail('');
    setSupport({ studio_name: '' });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            {branding.loading ? (
              <div className="h-14 mb-3" />
            ) : branding.logo_url ? (
              <img src={`${BACKEND_URL}${branding.logo_url}`} alt={branding.display_name || ''} className="h-14 mx-auto mb-3 object-contain" />
            ) : (
              <h1 className="text-3xl font-bold text-[#6b0f1a] mb-2" data-testid="member-login-brand-name">{branding.display_name || 'Stüdyo'}</h1>
            )}
            <p className="text-gray-600">Üye Portalı</p>
          </div>

          {/* ===== Login ===== */}
          {resetStep === 'none' && (
            <>
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefon Numarası</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b0f1a]"
                    placeholder="5xxxxxxxxx"
                    required
                    data-testid="phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Şifre</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b0f1a]"
                    required
                    data-testid="password-input"
                  />
                </div>
                <button type="submit" disabled={loading} className="w-full bg-[#6b0f1a] text-white py-3 rounded-lg font-semibold hover:bg-[#8a1422] disabled:opacity-50" data-testid="login-button">
                  {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                </button>
              </form>
              <div className="text-center mt-4">
                <button type="button" onClick={() => { setResetPhone(phone); setResetStep('enter-phone'); }} className="text-[#6b0f1a] text-sm hover:underline" data-testid="forgot-password-btn">
                  Şifremi unuttum
                </button>
              </div>
            </>
          )}

          {/* ===== Enter phone for reset ===== */}
          {resetStep === 'enter-phone' && (
            <form onSubmit={handleRequestReset} className="space-y-5" data-testid="reset-step-enter-phone">
              <div className="text-center">
                <div className="w-14 h-14 bg-[#6b0f1a]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#6b0f1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Şifre Sıfırlama</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Kayıtlı telefon numaranızı girin. Hesabınıza bağlı e-posta adresine sıfırlama bağlantısı göndereceğiz.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Telefon Numarası</label>
                <input
                  type="tel"
                  value={resetPhone}
                  onChange={(e) => setResetPhone(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6b0f1a]"
                  placeholder="5xxxxxxxxx"
                  required
                  autoFocus
                  data-testid="reset-phone-input"
                />
              </div>
              <button type="submit" disabled={resetLoading} className="w-full bg-[#6b0f1a] text-white py-3 rounded-lg font-semibold hover:bg-[#8a1422] disabled:opacity-50" data-testid="reset-request-submit">
                {resetLoading ? 'Gönderiliyor...' : 'Sıfırlama Bağlantısı Gönder'}
              </button>
              <button type="button" onClick={resetForm} className="w-full text-gray-500 text-sm hover:text-gray-700 py-2">
                ← Giriş sayfasına dön
              </button>
            </form>
          )}

          {/* ===== Success: email sent ===== */}
          {resetStep === 'email-sent' && (
            <div className="space-y-4 text-center" data-testid="reset-step-email-sent">
              <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-600"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">E-posta Gönderildi 📬</h3>
              <p className="text-sm text-gray-600">
                {maskedEmail ? (
                  <>Hesabınıza bağlı <strong>{maskedEmail}</strong> adresine şifre sıfırlama bağlantısı gönderildi.</>
                ) : (
                  <>Hesabınıza bağlı e-posta adresine şifre sıfırlama bağlantısı gönderildi.</>
                )}
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                ⏱ Bağlantı 15 dakika içinde geçerliliğini yitirecektir.
              </div>
              <p className="text-xs text-gray-500">E-posta 2-3 dakika içinde gelmezse spam klasörünü kontrol edin.</p>
              <button onClick={resetForm} className="text-[#6b0f1a] text-sm hover:underline">
                ← Giriş sayfasına dön
              </button>
            </div>
          )}

          {/* ===== No email on file — show studio contact CTA ===== */}
          {resetStep === 'no-email' && (
            <div className="space-y-4" data-testid="reset-step-no-email">
              <div className="text-center">
                <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-800">E-posta Bulunamadı</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Hesabınız için kayıtlı bir e-posta bulunamadı. Lütfen stüdyo yönetimi ile iletişime geçerek e-posta adresinizi sisteme eklemelerini isteyin. Ardından bu sayfadan tekrar şifre sıfırlama yapabilirsiniz.
                </p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-500 font-semibold uppercase">Stüdyo İletişim</p>
                <p className="text-base font-bold text-gray-800 mt-1">{support.studio_name}</p>
                {support.phone && <p className="text-sm text-gray-600">Tel: {support.phone}</p>}
              </div>
              <button onClick={handleContactStudio} disabled={!support.whatsapp && !support.phone} className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-40 transition-colors flex items-center justify-center gap-2" data-testid="contact-studio-whatsapp">
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp ile Stüdyoya Ulaş
              </button>
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-800">
                🔒 <strong>Güvenlik için:</strong> E-posta adresinizi sisteme eklemek yalnızca stüdyo yönetimi tarafından yapılabilir.
              </div>
              <button type="button" onClick={resetForm} className="w-full text-gray-500 text-sm hover:text-gray-700 py-2">
                ← Giriş sayfasına dön
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-gray-600 hover:text-[#6b0f1a]">← Personel Girişi</a>
          </div>
        </div>

        {/* Install-to-home-screen helper: Android live install button + iPhone fallback steps */}
        <InstallAppCard className="mt-4" />
      </div>
    </div>
  );
};

export default MemberLogin;
